import { lazy } from 'react';
import type { AppManifest } from '@/apps/types';
import { basename } from '@/fs/resolve';

/**
 * Paint.
 *
 * Not a singleton, for the same reason Notepad isn't: XP let you have as many
 * as you liked, and two windows side by side is half the point of a drawing
 * program.
 *
 * The canvas is genuinely writable — unlike every other document on this disk —
 * so File ▸ Save As PNG hands the bitmap to the browser's downloads folder
 * rather than pretending to write it back to C:.
 *
 * Args:
 *   file  string  path to a `.bmp`/`.png` in the virtual filesystem; omitted
 *                 means a blank 480 × 360 canvas
 */
export const paintApp: AppManifest = {
  id: 'paint',
  name: 'Paint',
  icon: '/assets/icons/paint.svg',
  component: lazy(() => import('./Paint')),

  defaultSize: { w: 700, h: 520 },
  minSize: { w: 460, h: 360 },
  resizable: true,
  maximizable: true,
  singleton: false,

  openWith: ['.bmp', '.png'],

  group: 'Accessories',

  // The component re-titles itself once it knows the document name; this is
  // what the title bar and taskbar button show until then.
  titleFor: (args) => {
    const name = basename(String(args.file ?? ''));
    const dot = name.lastIndexOf('.');
    return `${(dot > 0 ? name.slice(0, dot) : name) || 'untitled'} - Paint`;
  },
};
